import { FC } from 'react';
import styled from 'styled-components';

import { CollapsibleContent } from 'ui/collapsibleContent';
import { DownArrow, UpArrow } from 'ui/icons/navigation';
import { spacing } from 'ui/theme';

import { JsonBranchProps, NodeType } from '../types';
import { Leaf } from './Leaf';
import { Row } from './Row';

type BranchContainerProps = {
  isCollapsed: boolean;
  hasChildren: boolean;
};

const BranchContainer = styled.div<BranchContainerProps>`
  padding-bottom: ${({ isCollapsed, hasChildren }) =>
    !isCollapsed && hasChildren ? spacing.threeQuarter : 0};
`;

type BranchValueProps = {
  className?: string;
  depth?: number;
};

const BranchValue = styled(CollapsibleContent)<BranchValueProps>`
  border-left: 1px solid transparent;
  margin-left: ${spacing.threeEights};
  padding-left: ${spacing.plusQuarter};
`;

export const Branch: FC<React.PropsWithChildren<JsonBranchProps>> = ({
  CollapseButton,
  children,
  inArray,
  isCollapsed,
  isLast,
  label,
  type,
  value,
  depth = 0,
}) => {
  const Arrow = isCollapsed ? DownArrow : UpArrow;

  const arrayLength = type === NodeType.array ? value.length : 0;
  const objectLength = type === NodeType.object ? Object.keys(value).length : 0;

  const trailingComma = isLast ? '' : ',';
  let branchValue = '';
  let branchTail = '';

  switch (type) {
    case NodeType.array:
      branchValue = '[';
      branchTail = `]${trailingComma}`;
      break;

    case NodeType.object:
      branchValue = '{';
      branchTail = `}${trailingComma}`;
      break;

    default:
      break;
  }

  return (
    <BranchContainer
      isCollapsed={isCollapsed}
      hasChildren={Boolean(arrayLength) || Boolean(objectLength)}
      className="json-branch json-row"
      data-test="json-branch-raw"
    >
      <Leaf
        inArray={inArray}
        indent={false}
        label={label}
        startDecoration={
          <CollapseButton>
            <Arrow height={12} width={12} />
          </CollapseButton>
        }
        type={type}
        value={branchValue}
      />
      <BranchValue isCollapsed={isCollapsed} depth={depth} data-test="json-branch-value">
        {children}
      </BranchValue>
      <Row>{branchTail}</Row>
    </BranchContainer>
  );
};
